/**
 * @file EvidenceCard.tsx
 *
 * @description This component displays details of a video evidence file and allows officers to process,
 * view, or select videos for report generation. Part of the police evidence management system.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 * @requires js-cookie
 * @requires react-router-dom
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Tooltip,
  AlertTitle,
  CardMedia,
  Chip,
  Stack,
  Grid,
  alpha,
  useTheme,
} from "@mui/material";
import { VideoPreview } from "../../shared/interfaces/VideoPreview";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import {
  PlayArrow,
  Downloading as Download,
  VideoLibrary,
  CalendarToday,
  AccessTime,
  Videocam,
  Description,
  Label,
} from "@mui/icons-material";
import { useToast } from "../../shared/toast";
import { VideoStatus } from "../../shared/interfaces/VideoData";
import { formatDuration } from "../../shared/utils/formatDuration";
import { bodycamClient } from "../../api/clients";
import audioThumbnailLight from "../../assets/audio_thumbnail_light.svg";
import audioThumbnailDark from "../../assets/audio_thumbnail_dark.svg";
import { MAX_VIDEOS_PER_REPORT } from "./hooks/useVideoSelection";
/**
 * Props for the EvidenceCard component
 * @interface EvidenceCardProps
 * @property {VideoPreview} video - The video evidence data to display
 * @property {boolean} [isSelected=false] - Whether this card is currently selected
 * @property {Function} [onSelect] - Callback when the card is selected/deselected
 * @property {number} numberSelected - How many cards are currently selected in the parent component
 */
interface EvidenceCardProps {
  video: VideoPreview;
  isSelected?: boolean;
  onSelect?: (videoId: string, isSelected: boolean) => void;
  numberSelected: number;
}

/**
 * @name EvidenceCard
 *
 * @description Card component that displays a single piece of video evidence with its metadata
 * and provides controls to process, view, or select it for batch operations.
 *
 * The card has different visual states based on selection status and video fetching status.
 * It enforces a maximum of 5 videos can be selected at once through the parent component's counter.
 *
 * @param {EvidenceCardProps} props - Component props
 *
 * @returns {React.ReactElement} Rendered evidence card
 */
export const EvidenceCard: React.FC<EvidenceCardProps> = ({
  video,
  isSelected = false,
  onSelect,
  numberSelected,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const [thumbnailError, setThumbnailError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const isAudio = video.fileType?.startsWith("audio") ?? false;

  useEffect(() => {
    let objectUrl: string | null = null;

    // Skip thumbnail fetch for videos still processing — no thumbnail exists yet
    if (
      video.status === VideoStatus.FETCHING ||
      video.status === VideoStatus.COMPRESSING
    ) {
      return;
    }

    // Audio evidence uses a preset waveform thumbnail; no backend frame to fetch
    if (isAudio) {
      return;
    }

    const fetchThumbnail = async () => {
      try {
        setThumbnailError(false);
        const response = await bodycamClient.get("/thumbnail", {
          params: { video_id: video.id },
        });

        setThumbnailUrl(response.data.url);
      } catch (error) {
        console.error("Failed to fetch thumbnail:", error);
        setThumbnailError(true);
      }
    };

    fetchThumbnail();

    // Cleanup: revoke the object URL when component unmounts or video changes
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [video.id, video.status, isAudio]);

  // Whether the video is ready to be viewed or processed
  const isVideoReady = [
    VideoStatus.READY,
    VideoStatus.TRANSCRIPT_READY,
    VideoStatus.TRANSCRIBING,
  ].includes(video.status);

  // Whether the video is currently being processed by the system
  const isFetching = video.status === VideoStatus.FETCHING;
  const isCompressing = video.status === VideoStatus.COMPRESSING;
  /**
   * Whether the card can be selected based on status and selection limits
   * A card is selectable if it's ready and either already selected or
   * there are fewer than 5 selections total
   */
  const isSelectable = isVideoReady && (numberSelected < MAX_VIDEOS_PER_REPORT || isSelected);

  // Determine the appropriate color to display based on video fetching status
  const getStatusColor = () => {
    switch (video.status) {
      case VideoStatus.TRANSCRIPT_READY:
      case VideoStatus.READY:
        return "success";
      case VideoStatus.COMPRESSING:
      case VideoStatus.FETCHING:
      case VideoStatus.TRANSCRIBING:
        return "warning";
      default:
        return "error";
    }
  };

  /**
   * @name getAutoThumbnail
   *
   * @description Returns correct thumbnail URL based on whether the evidence is audio or video.
   *  Audio files use a preset thumbnail image, while video files use the fetched thumbnail URL.
   *
   * @returns {string} URL of the appropriate thumbnail image
   */
  const getAutoThumbnail = () => {
    if (isAudio) {
      return theme.palette.mode === "dark"
        ? audioThumbnailDark
        : audioThumbnailLight;
    }
    return thumbnailUrl;
  }

  /**
   * @name formatRecordedDateTime
   *
   * @description Formats a recorded date string into a user-friendly date and time
   *
   * @param {string} dateString - ISO date string representing when the video was recorded
   *
   * @returns {string} Formatted date and time or the original string if parsing fails
   */
  const formatRecordedDateTime = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    const formattedDate = date.toLocaleDateString(undefined);
    const formattedTime = date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return `${formattedDate} at ${formattedTime.toLowerCase()}`;
  };

  // Handles clicks on the main action button based on video status
  const handleButtonClick = () => {
    if (isVideoReady) {
      navigate(`/generate-report/${video.id}`);
      return;
    } else if (video.status === VideoStatus.FETCHING) {
      toast.error(
        "This video is currently fetching. Please wait for it to finish.",
      );
      return;
    }
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
      {/* Main evidence card */}
      <Card
        sx={{
          height: "100%",
          width: "100%",
          minWidth: { xs: "200px", xxl: "240px", uhd: "280px" },
          maxWidth: { xs: "300px", xxl: "360px", uhd: "420px" },
          margin: "auto",
          borderRadius: "12px",
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.12)
            : numberSelected > 0 && !isSelectable
              ? "action.disabledBackground"
              : "background.paper",
          transition: "all 0.3s ease",
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : 1,
          borderColor: isSelected ? "primary.main" : "divider",
          "&:hover": {
            transform: isSelectable ? "translateY(-4px)" : "none",
            boxShadow: isSelectable ? "0 4px 20px rgba(0, 0, 0, 0.15)" : "none",
            transition: "all 0.3s ease",
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          cursor: isSelectable ? "pointer" : "default",
          opacity: numberSelected > 0 && !isSelectable ? 0.7 : 1,
          position: "relative",
        }}
        onClick={() => {
          if (isSelectable && onSelect) {
            onSelect(video.id, !isSelected);
          }
        }}
      >
        <Box sx={{ position: "relative" }}>
          {isFetching || isCompressing ? (
            <Alert
              severity="info"
              icon={false}
              sx={{
                height: { xs: 140, xxl: 170, uhd: 200 },
                borderRadius: "12px 12px 0 0",
                alignItems: "center",
                "& .MuiAlertTitle-root": {
                  fontWeight: "bold",
                  textAlign: "center",
                  width: "100%",
                  fontSize: { xs: "0.9rem", xxl: "1rem", uhd: "1.1rem" },
                },
                "& .MuiAlert-message": {
                  textAlign: "center",
                  width: "100%",
                  fontSize: { xs: "0.75rem", xxl: "0.85rem", uhd: "0.95rem" },
                },
              }}
            >
              <AlertTitle>Processing</AlertTitle>
              Please wait...
            </Alert>
          ) : !thumbnailError && (thumbnailUrl || isAudio) ? (
            <CardMedia
              component="img"
              image={getAutoThumbnail()}
              alt={video.fileName}
              sx={{
                objectFit: "cover",
                height: { xs: 140, xxl: 170, uhd: 200 },
              }}
              onError={() => setThumbnailError(true)}
            />
          ) : (
            <Box
              sx={{
                height: { xs: 140, xxl: 170, uhd: 200 },
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <VideoLibrary sx={{ fontSize: 40 }} />
            </Box>
          )}
          <Chip
            label={video.status}
            color={getStatusColor()}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              fontWeight: "bold",
              boxShadow: 1,
            }}
          />
        </Box>

        <CardContent sx={{ p: 2, flexGrow: 1 }}>
          <Stack spacing={1.5} mt={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Description
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Tooltip title={video.fileName} placement="top">
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {video.fileName}
                </Typography>
              </Tooltip>
            </Box>

            {video.label && (
              <Box display="flex" alignItems="center" gap={1.5}>
                <Label
                  fontSize="small"
                  sx={{ color: "text.secondary", fontSize: "1rem" }}
                />
                <Tooltip title={video.label} placement="top">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {video.label}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarToday
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography variant="body2" color="text.primary">
                {formatRecordedDateTime(video.recordedDate)}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <AccessTime
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography variant="body2" color="text.primary">
                {formatDuration(video.duration)}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <Videocam
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {video.device || "Manual Upload"}
              </Typography>
            </Box>
          </Stack>
        </CardContent>

        {/* Action button section */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            loading={isFetching || isCompressing}
            disabled={isFetching || isCompressing}
            variant="contained"
            onClick={handleButtonClick}
            startIcon={
              video.status === VideoStatus.READY ||
              video.status === VideoStatus.TRANSCRIPT_READY ? (
                <PlayArrow />
              ) : (
                <Download />
              )
            }
            sx={{
              width: "100%",
              textTransform: "none",
              color: "#fff",
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%)`,
              boxShadow: `0 4px 14px 0 ${alpha(
                theme.palette.primary.main,
                0.3,
              )}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.light,
                  0.9,
                )} 0%, ${theme.palette.primary.light} 50%)`,
                boxShadow: `0 6px 20px 0 ${alpha(
                  theme.palette.primary.main,
                  0.4,
                )}`,
              },
              "&.MuiButton-loading": {
                background: `${theme.palette.primary.dark} !important`,
                backgroundColor: `${theme.palette.primary.dark} !important`,
              },
              "&&.Mui-disabled": {
                background: `${theme.palette.primary.dark} !important`,
                backgroundColor: `${theme.palette.primary.dark} !important`,
              },
              "& .MuiButton-loadingIndicator": {
                color: "#fff !important",
              },
              "&& .MuiCircularProgress-root": {
                color: "#fff !important",
              },
              borderRadius: 2,
              py: 1,
              mb: 1.5,
            }}
          >
            {isVideoReady
              ? "View Video"
              : isFetching
                ? "Fetching..."
                : isCompressing
                  ? "Compressing..."
                  : video.status === VideoStatus.TRANSCRIBING
                    ? "Transcribing..."
                    : "Process Video Now"}
          </Button>

          <Typography
            variant="caption"
            display="block"
            align="center"
            sx={{ color: "text.secondary" }}
          >
            Uploaded:{" "}
            {new Date(video.uploadedDate).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </Typography>
        </Box>
      </Card>
    </Grid>
  );
};
